import { USE_FEDERATED_AUTH } from 'config';
import { useGet } from 'lib/api';
import { useUser } from 'features/auth';
import { apiUsers } from 'api/users';
import type { LoggedInUser, Module } from '../types';

type LoggedInUserAccess = {
  modules: Module[] | null;
  roleNames: string[] | null;
  name?: string;
  avatarColor?: string;
};

// Single source for the access-gating inputs (modules / roles / display identity).
// The two auth modes have different backends with different wire contracts, so each mode
// keeps its own typed source: federated reads the Nexos current-user data already fetched
// into the login context (GET /api/users/me via AuthProviderFederated), legacy reads
// /LoggedInUser. The legacy query never fires under federated auth — D2 must not call the
// legacy Api2 /api/user endpoint in federated mode.
export const useLoggedInUser = (): LoggedInUserAccess => {
  const { data: user } = useUser();
  const { data } = useGet<LoggedInUser>(
    apiUsers.get.loggedInUser,
    'logged-in-user',
    [],
    undefined,
    true,
    { enabled: !USE_FEDERATED_AUTH }
  );

  if (USE_FEDERATED_AUTH) {
    const context = user?.loginContext;
    return {
      // checkModule (and the e2e modules-transform fixture) match on the legacy PascalCase
      // Module shape — map the Nexos camelCase wire shape into it at this edge.
      modules: context?.modules?.map(m => ({ Id: m.id, Key: m.key, Name: m.name })) ?? null,
      roleNames: context?.roleNames ?? null,
      name: context?.name,
      avatarColor: context?.avatarColor,
    };
  }

  return {
    modules: data?.Organization?.Modules ?? null,
    roleNames: data?.RoleNames ?? null,
    name: data?.name,
    avatarColor: data?.AvatarColor,
  };
};
